import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { AnalysisSelection } from "./combinationAnalyzer";

// "Analiz Listem" store. Selections are persisted locally (localStorage) behind
// this provider. When the owner supplies the auth contract, the storage layer can
// be swapped for an account-backed list without changing the UI.

interface AnalysisCtx {
  items: AnalysisSelection[];
  count: number;
  has: (fixtureId: string, marketCode: string) => boolean;
  add: (s: Omit<AnalysisSelection, "addedAt">) => void;
  remove: (fixtureId: string, marketCode: string) => void;
  clear: () => void;
}

const KEY = "sdip.analysislist";

function load(): AnalysisSelection[] {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
}

const Ctx = createContext<AnalysisCtx>({} as AnalysisCtx);

export const AnalysisListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<AnalysisSelection[]>(load);

  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {} }, [items]);

  const has = useCallback((f: string, m: string) => items.some((x) => x.fixtureId === f && x.marketCode === m), [items]);

  const add = useCallback((s: Omit<AnalysisSelection, "addedAt">) => {
    setItems((prev) => prev.some((x) => x.fixtureId === s.fixtureId && x.marketCode === s.marketCode)
      ? prev
      : [{ ...s, addedAt: Date.now() }, ...prev]);
  }, []);

  const remove = useCallback((f: string, m: string) => {
    setItems((prev) => prev.filter((x) => !(x.fixtureId === f && x.marketCode === m)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return (
    <Ctx.Provider value={{ items, count: items.length, has, add, remove, clear }}>
      {children}
    </Ctx.Provider>
  );
};

export function useAnalysisList() { return useContext(Ctx); }
